import React, { useCallback, useMemo, useState } from 'react';
import { goToDetailAction } from '../utils';
import { getChartTimeParams } from '@op/utils/time';
import BubbleCommon, {
  commonClickDetailPageTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import { SymbolType } from '@op/constants/chart';
import _ from 'lodash-es';
import { selectable } from 'tea-component/lib/table/addons';
import { useHistory } from 'umi';
import { DetailPage } from '../types';

type ParamsValueItem =
  | string
  | { key: string; isArray?: boolean; isLineSelected?: boolean };
type ParamsValueType = Record<string, ParamsValueItem>;
// 定义参数处理器类型
type ParamProcessor = (value: any, key: string) => any;
export interface EventDetailProps {
  key: string; // 下转页配置的key
  filter: React.MutableRefObject<any>; // 下转页面的参数
  chartType: SymbolType;
  paramsList: Partial<Record<SymbolType, ParamsValueType>>; // 多图表的情况下，可以用这个参数批量传入
  isDetail?: boolean; // 是否为详情页面
  updateDetailFilter?: (params: any) => void; // 修改详情页面参数
  defaultProps?: React.MutableRefObject<any>; // 组件默认参数
  defaultConfig?: React.MutableRefObject<any>; // 组件默认配置
  componentProps?: React.MutableRefObject<any>; // 包裹组件的参数
  finalDealwithParams?: (
    params: any,
    options: {
      event: any;
      filter: any;
      chartType: SymbolType;
      componentProps?: any;
    },
  ) => any; // 最终处理参数
  hasDetailPage: React.MutableRefObject<boolean>; // 可以跳转到详情页面
  finalDealwithDetailParams?: (
    params: DetailPage,
    options: { chartType?: SymbolType; componentProps?: any },
  ) => DetailPage; // 最终处理跳转详情页面的参数
}

const sentimentValue: any = {
  Positive: [5],
  Neutral: [3],
  Negative: [1],
};

export function useDetailEvent({
  key,
  filter,
  chartType,
  paramsList,
  isDetail = false,
  updateDetailFilter,
  defaultProps,
  defaultConfig,
  hasDetailPage,
  finalDealwithParams = (params: any) => params,
  finalDealwithDetailParams = (params: DetailPage) => params,
  componentProps,
}: EventDetailProps) {
  const [extraProps, setExtraProps] = useState<any>();
  const history = useHistory();

  const goPage = useCallback(
    (params: any) => {
      if (isDetail) {
        updateDetailFilter?.(params);
        return;
      }
      if (!hasDetailPage.current) return;

      const finalParams = finalDealwithDetailParams?.(
        {
          key,
          filter: filter.current,
          defaultProps: {
            ...(defaultProps?.current || {}),
            ...extraProps,
            ...params,
          },
          extraConfig: { ...(defaultConfig?.current || {}), ...params },
        },
        { chartType, componentProps: componentProps?.current },
      );
      goToDetailAction(finalParams, {
        history,
        stats: { reportName: history.location.pathname },
      });
    },
    [isDetail, chartType],
  );

  // 特殊参数处理器映射对象
  const paramProcessors: Record<string, ParamProcessor> = {
    // 情感值处理器
    sentiment: (value: any, key: string) => {
      return { [key]: sentimentValue[value] || [] };
    },
    // 时间处理器
    time: (value: any) => {
      const timeObj = getChartTimeParams(
        value,
        filter.current?.date_type || 'daily',
      );
      return { ...timeObj };
    },
  };

  function dealwithParams(event: any, type = chartType) {
    const obj = paramsList[type];
    const params = {};
    _.forEach(obj, (item: ParamsValueItem, key: any) => {
      const { key: valueKey, isArray } =
        typeof item === 'string' ? { key: item, isArray: true } : item;

      const value = event[valueKey];
      if (!value) return;
      // 特殊参数的内容进行处理
      if (paramProcessors[key]) {
        const processor = paramProcessors[key];
        const processorParams = processor(value, key);
        _.merge(params, processorParams);
        return;
      }

      // 如果不是数组，但是需要数组，则转换为数组
      if (!Array.isArray(value) && isArray) {
        _.merge(params, { [key]: [value] });
        return;
      }

      // 普通情况，直接合并
      _.merge(params, { [key]: value });
    });

    // 如果需要处理参数，最终处理一下
    return finalDealwithParams(params, {
      filter: filter.current,
      event,
      chartType: type,
      componentProps: componentProps?.current,
    });
  }

  const getEventsDict = useCallback(
    (type = chartType) => {
      if (type === 'table') {
        return {
          onClick: (record: any) => {
            const params = dealwithParams(record, 'table');
            console.log(record, 'record', params);
            goPage(params);
          },
          addons: [
            selectable({
              onChange: (_value: string[], context: any) => {
                const { record } = context || {};
                const params = dealwithParams(record, 'table');
                goPage(params);
              },
              rowSelect: true,
            }),
          ],
        };
      }

      return {
        click: (event: any) => {
          console.log('event', event);
          const params = dealwithParams(event);
          goPage(params);
        },
        legendselectchanged: (params: { selected: any }) => {
          const defaultSelected = params.selected;
          setExtraProps({ defaultSelected });
        },
      };
    },
    [chartType],
  );

  const eventsDict = useMemo(() => {
    return getEventsDict(chartType);
  }, [getEventsDict]);

  const onChartReady = useCallback((chart: any) => {
    chart.getZr().on('click', (zrEvent: any) => {
      if (zrEvent?.target) return;
      if (chartType !== 'sentiment-s-bar') return;
      const point = [zrEvent.offsetX, zrEvent.offsetY];
      if (!chart.containPixel('grid', point)) return;

      const [_xValue, yValue] = chart.convertFromPixel(
        { seriesIndex: 0 },
        point,
      );
      const option = chart.getOption();

      const specialParamsKey: ParamsValueType = paramsList['sentiment-s-bar']!;
      if (!specialParamsKey) {
        return;
      }
      let item: any = '';
      _.forEach(specialParamsKey, (value: ParamsValueItem) => {
        if (typeof value === 'string') {
          return;
        }
        if (value.isLineSelected) {
          item = value;
        }
      });
      if (item) {
        const yAxisData = option.yAxis[0].data;
        const index = Math.round(yValue);

        if (index >= 0 && index < yAxisData?.length) {
          const selected = yAxisData[index];
          // 取值逻辑 & 后续可加新paramsKey处理如果多chartType
          const params = dealwithParams({ [item.key]: selected });
          goPage(params);
        }
      }
    });
  }, []);

  /**
   * 放大表格的通用组件
   * @param element
   * @param tips
   * @param options
   * @returns
   */
  const fullTableWrapper = useCallback(
    (
      element: any,
      tips: TipsMapType = commonClickDetailPageTips,
      options = {},
    ) => {
      // 如果是详情页面，并且不能跳转到详情页面，则直接返回
      if (isDetail || !hasDetailPage.current) {
        return <div className="dbo-detail-wrapper-table">{element}</div>;
      }

      return (
        <BubbleCommon
          placement="top"
          tipsMap={tips}
          showIcon={false}
          {...options}
        >
          <div className="dbo-detail-wrapper-table">{element}</div>
        </BubbleCommon>
      );
    },
    [],
  );

  return {
    eventsDict,
    goPage,
    getEventsDict,
    fullTableWrapper,
    onChartReady,
  };
}
